import { LoadableComponent } from "@loadable/component";
import { SwanStyleSheetKey } from "@vp/swan";
import { ReactChild } from "react";
import { ExperimentImpression } from "../contexts/AnalyticsContextProvider";
import { PageStaticDataContextType } from "../contexts/PageStaticDataContextProvider";
import PluginRegistry from "./PluginRegistry";

export interface PlaceholderPlugin {
  init(pluginRegistry: PluginRegistry): void;
  pluginOwner: PluginOwner;
}

export enum Placeholder {
  Top = "Top",
  AfterDescription = "AfterDescription",
  AfterPricing = "AfterPricing",
  Pricing = "Pricing",
  AfterTab = "AfterTab",
  QuantityDropdown = "QuantityDropdown",
  Ctas = "Ctas",
  AfterCtas = "AfterCtas",
  ProductBuilder = "ProductBuilder",
  ProductConfigurator = "ProductConfigurator",
  ProductBuilderHeaderRight = "ProductBuilderHeaderRight",
  Hero = "Hero",
  ReferentialImageThumbnail = "ReferentialImageThumbnail",
  ReferentialImage = "ReferentialImage",
}

export enum PlaceholderGroup {
  CtaGroup = "CtaGroup",
}

export enum PlaceholderGroupMember {
  CtaAddToCart = "CtaAddToCart",
  CtaAlbelliGallery = "CtaAlbelliGallery",
  CtaAlbelliStudio = "CtaAlbelliStudio",
  CtaBulkOrder = "CtaBulkOrder",
  CtaGallery = "CtaGallery",
  CtaStudio = "CtaStudio",
  CtaStudioBlank = "CtaStudioBlank",
  CtaStudioCustom = "CtaStudioCustom",
  CtaStudioFullBleed = "CtaStudioFullBleed",
  CtaStudioTemplate = "CtaStudioTemplate",
  CtaNinetyNineDesigns = "CtaNinetyNineDesigns",
  CtaUploadAndGo = "CtaUploadAndGo",
  CtaUploadAndPrint = "CtaUploadAndPrint",
  CtaDesignAssistant = "CtaDesignAssistant",
  CtaEditTemplate = "CtaEditTemplate",
}

export const PlaceholderGroupRelationship: Record<PlaceholderGroup, PlaceholderGroupMember[]> = {
  [PlaceholderGroup.CtaGroup]: [
    PlaceholderGroupMember.CtaAddToCart,
    PlaceholderGroupMember.CtaAlbelliGallery,
    PlaceholderGroupMember.CtaAlbelliStudio,
    PlaceholderGroupMember.CtaBulkOrder,
    PlaceholderGroupMember.CtaGallery,
    PlaceholderGroupMember.CtaNinetyNineDesigns,
    PlaceholderGroupMember.CtaStudio,
    PlaceholderGroupMember.CtaStudioBlank,
    PlaceholderGroupMember.CtaStudioCustom,
    PlaceholderGroupMember.CtaStudioFullBleed,
    PlaceholderGroupMember.CtaStudioTemplate,
    PlaceholderGroupMember.CtaUploadAndGo,
    PlaceholderGroupMember.CtaUploadAndPrint,
    PlaceholderGroupMember.CtaDesignAssistant,
    PlaceholderGroupMember.CtaEditTemplate,
  ],
};

export enum PlaceholderGroupValidations {
  AllPlaceholdersFilled = "AllPlaceholdersFilled",
  AtLeastOnePlaceholderRendered = "AtLeastOnePlaceholderRendered",
}

export interface PlaceholderGroupDataType<T> {
  type: PlaceholderGroupMember;
  data: T;
}

export interface PluginOwner {
  ownerName: string;
  email: string;
  slack: string;
  issueEscalation: string;
}

export interface ComponentToBeRegistered<T> {
  placeholder: Placeholder | PlaceholderGroupMember;
  Component: LoadableComponent<T>;
  experimentContext?: PluginExperimentContext;
  name: string;
  dataNeeds?: PluginDataNeeds;
  styleNeeds?: SwanStyleSheetKey[];
  enable: (state: PluginEnableContext, parentComponents: string[]) => boolean;
}

export interface RegisteredComponent<T> {
  name: string;
  placeholder: Placeholder | PlaceholderGroupMember;
  Component: LoadableComponent<T>;
  experimentContext?: PluginExperimentContext;
  dataNeeds?: PluginDataNeeds;
  styleNeeds?: SwanStyleSheetKey[];
  enable: (state: PluginEnableContext, parentComponents: string[]) => boolean;
  pluginOwner: PluginOwner;
}

export interface PluginExperimentContext {
  experimentKey: string;
  // Render component in these variations
  renderTestExperienceForVariations: string[];
  // These should be a super set of renderTestExperienceForVariations,
  // and should include control experience, where it does not render component, but fire impression
  fireImpressionForVariations: string[];
  // When undefined, it means all locales are supported.
  supportedLocales?: string[];
  // When undefined, it means all products are supported.
  supportedMpvIds?: string[];
}

export interface PluginEnableContext {
  pageProps: PageStaticDataContextType;
  experimentImpression?: ExperimentImpression;
  decision?: string;
}

export enum SupportedPluginContentfulDataType {
  DictionaryEntry = "DictionaryEntry",
  DictionaryEntryMarkdown = "DictionaryEntryMarkdown",
  SimpleLink = "SimpleLink",
  CloudinaryImageComponent = "CloudinaryImageComponent",
  CloudinaryVideoComponent = "CloudinaryVideoComponent",
  Other = "Other",
}

export type CloudinaryTransformationsDefinition = { [label: string]: string };

export interface ContentfulCloudinaryDataNeed extends ContentfulDataNeed {
  transformations?: CloudinaryTransformationsDefinition;
  transformationsMcs?: CloudinaryTransformationsDefinition;
  transformationsMcsView?: string;
}

export interface ContentfulOtherDataNeed extends ContentfulDataNeed {
  contentTypeName: string;
  graphqlQueryBody: string;
}

export interface ContentfulDataNeed {
  contentfulId?: string;
  contentfulName?: string;
  contentType: SupportedPluginContentfulDataType;
}

export type ContentfulDataNeeds = {
  [dataName: string]: ContentfulDataNeed | ContentfulCloudinaryDataNeed;
};

export interface PluginDataNeeds {
  contentful: ContentfulDataNeeds;
}

export type PluginData = {
  contentful: {
    [dataName: string]: any;
  };
};

export interface PluginStateDefinition {
  name: string;
  contextProvider: (props: {
    children: ReactChild | ReactChild[] | undefined | null;
  }) => JSX.Element;
}
